import styled from '@emotion/styled';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { getMetricColor } from '@resistapp/client/utils/metric-utils';
import { MetricMode } from '@resistapp/common/types';
import { AxisLeft } from '@visx/axis';
import { LinearGradient } from '@visx/gradient';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';

// Define positioning object similar to what's in chart-styles.tsx
const positioning = {
  margin: {
    top: 40,
    right: 30,
    bottom: 0,
    left: 100,
  },
};

// Scale ticks from -5 to 1
const scaleTicks = [-5, -4, -3, -2, -1, 0, 1];

// Labels for each tick value
const getTickLabel = (value: number): string => {
  switch (value) {
    case 1:
      return '(+900%) 1.0';
    case 0:
      return '(±0%) 0.0';
    case -1:
      return '(-90%) -1.0';
    case -2:
      return '(-99%) -2.0';
    case -3:
      return '(-99.9%) -3.0';
    case -4:
      return '-4.0';
    case -5:
      return ' -5.0';
    default:
      return `${value}`;
  }
};

export function ReductionIllustration() {
  const { activeAbundanceMode } = useOverviewContext();
  const indexHeight = 400;
  const graphHeight = indexHeight - positioning.margin.top - positioning.margin.bottom;
  const svgPadding = { left: 95, right: 20 }; // Increased left padding for tick labels
  const contentWidth = 510; // Width for axis, bar, and text
  const svgWidth = contentWidth + svgPadding.left + svgPadding.right; // Total SVG width
  const descriptionX = 38; // Start descriptions slightly further from the bar
  const descriptionWidth = contentWidth - descriptionX; // Calculate description width

  // Create a linear scale for the Y axis (-5 to 1)
  const yScale = scaleLinear({
    domain: [-5, 1],
    range: [graphHeight, 0], // Invert range for SVG coordinates (0 is top)
  });

  const offset = 10;

  // Manually render the tick labels
  const renderTickLabels = () => {
    return scaleTicks.map(value => (
      <text key={value} x={-10} y={yScale(value)} dx="-0.5em" dy="0.33em" fontSize={14} textAnchor="end" fill="black">
        {getTickLabel(value)}
      </text>
    ));
  };

  return (
    <ReductionScaleContainer>
      <svg width={svgWidth} height={indexHeight} style={{ overflow: 'visible' }}>
        <Group left={svgPadding.left} top={positioning.margin.top / 2}>
          {/* Custom tick labels */}
          {renderTickLabels()}

          {/* Tick marks */}
          <AxisLeft
            scale={yScale}
            left={0}
            hideAxisLine={true}
            tickStroke={'white'}
            numTicks={scaleTicks.length}
            tickValues={scaleTicks}
            tickFormat={() => ''} // Hide default labels
            tickLength={0} // Hide tick marks
          />

          {/* The colored reduction score bar */}
          <defs>
            <LinearGradient id="reduction-info-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={getMetricColor(-5, MetricMode.REDUCTION, activeAbundanceMode)} />
              <stop offset="83%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor={getMetricColor(1, MetricMode.REDUCTION, activeAbundanceMode)} />
            </LinearGradient>
          </defs>
          <rect x={0} y={0} width={40} height={graphHeight} fill="url(#reduction-info-gradient)" rx={5} />

          {/* Level Descriptions */}
          <foreignObject x={descriptionX} y={yScale(1) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Genes strive in the treatment process (e.g. septic tank)</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(0) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— No change in the total abundance of the selected gene(s)</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(-1) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Poor reduction compared to most genes in European WWTPs</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(-2) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Average reduction across all genes in European WWTPs</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(-3) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Relatively good reduction, though average for some genes</LevelReference>
          </foreignObject>
        </Group>
      </svg>
    </ReductionScaleContainer>
  );
}

const ReductionScaleContainer = styled.div`
  padding-top: 10px;
  width: 100%;
  display: flex;
  position: relative;
`;

const LevelReference = styled.div`
  font-size: 14px;
  color: #364153;
  padding-left: 10px; // Space from the color bar
`;
